import React from 'react';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { PanelBar, PanelBarItem } from '@progress/kendo-react-layout';

import {
  GetResourceValue,
  UpdateRoutesAsync,
  UpdateGameDocState
} from '../../../utils/game-document';
import PopupMenu from '../../popupMenu';
import { MapConfigStore } from '../../../pages/game-designer';
import { GameDocumentContext } from '../../../contexts/game-document';
import { TaskRoute } from '../../../types/game-document/entities/routes';
import { MenuList } from '../../../features/game-document/maps/map-images-window';

const annotationkMenu: MenuList[] = [
  { classIcon: 'edit', textMenu: 'Edit', textClass: '' },
  { classIcon: 'delete', textMenu: 'Delete', textClass: 'text-danger' }
];

export const Annotations: React.FC<{
  routes: TaskRoute[];
  searchContentText: string;
  handleRouteMenuSelected: (id: string, menu: MenuList) => void;
}> = ({ routes, searchContentText, handleRouteMenuSelected }) => {
  const { gameId } = useParams();

  const [mapConfig, setMapConfig] = useLocalStorage<MapConfigStore>(
    `map-config-${gameId}`,
    {}
  );

  const [state, setState] = React.useContext(GameDocumentContext);
  const { gameDocument } = state;

  const filteredRoutes = React.useMemo(() => {
    return routes.filter((annotation: TaskRoute) => {
      const routeTitle = GetResourceValue(
        gameDocument!,
        annotation.titleResId ?? ''
      );
      return routeTitle.toLowerCase().includes(searchContentText.toLowerCase());
    });
  }, [routes, gameDocument, searchContentText]);

  const showRoute = (route: TaskRoute, isVisible: boolean) => {
    if (!state.gameDocument) return;
    route.isVisible = isVisible;
    const result = UpdateRoutesAsync(state?.gameDocument!, route);
    setState((prev) => UpdateGameDocState(prev, result));
  };

  if (
    filteredRoutes.length === 0 ||
    (searchContentText && filteredRoutes.length === 0)
  )
    return null;

  return (
    <PanelBar
      onSelect={() => {
        setMapConfig((prev) => ({
          ...prev,
          mapContentAnnotations: !prev.mapContentAnnotations
        }));
      }}>
      <PanelBarItem
        expanded={mapConfig.mapContentAnnotations}
        className={'panel-bar-custom'}
        title={
          <div className={'d-flex gap-2 align-items-center'}>
            <span
              className="material-symbols-outlined"
              style={{
                color: '#A8B6BE'
              }}>
              arrow_split
            </span>
            Annotations
          </div>
        }>
        {filteredRoutes.map((annotation, index) => (
          <div
            key={annotation.id}
            className={
              'd-flex w-100 justify-content-between align-items-center mt-1 gap-2'
            }>
            <div
              className={'card w-100 border-0'}
              style={{
                backgroundColor: '#A8B6BE'
              }}>
              <div
                className={
                  'card-body p-2 d-flex w-100 justify-content-between align-items-center gap-2'
                }>
                <div
                  className={'d-flex gap-2 text-light align-items-center'}
                  style={{
                    minWidth: 0,
                    flex: 1
                  }}>
                  <span className={'material-symbols-outlined flex-shrink-0'}>
                    arrow_split
                  </span>
                  <span
                    title={GetResourceValue(
                      gameDocument!,
                      annotation.titleResId ?? ''
                    )}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0
                    }}>
                    {GetResourceValue(
                      gameDocument!,
                      annotation.titleResId ?? ''
                    )}
                  </span>
                </div>
                <div className={'d-flex flex-shrink-0 align-items-center'}>
                  {annotation.automaticallyInfo && (
                    <OverlayTrigger
                      overlay={
                        <Tooltip id={annotation.id}>
                          {annotation.automaticallyInfo}
                        </Tooltip>
                      }>
                      <div
                        style={{
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#4698cb',
                          width: '25px',
                          height: '25px',
                          borderRadius: '100%'
                        }}>
                        <span
                          className={'material-symbols-outlined'}
                          style={{
                            fontSize: '16px',
                            color: 'white',
                            cursor: 'default'
                          }}>
                          {'bolt'}
                        </span>
                      </div>
                    </OverlayTrigger>
                  )}
                  <PopupMenu
                    id={index}
                    buttonColor="white"
                    menus={annotationkMenu}
                    onMenuSelected={(id, menu) => {
                      handleRouteMenuSelected(routes[id].id, menu);
                    }}
                    iconClass={'k-icon k-i-more-vertical h5 mb-0 text-light'}
                  />
                </div>
              </div>
            </div>

            <span
              onClick={() => {
                showRoute(annotation, !annotation.isVisible);
              }}
              className={`text-primary material-symbols-outlined`}>
              <span
                className={`${annotation.isVisible ? 'text-primary' : 'text-danger'} material-symbols-outlined cursor-pointer text-decoration-none`}>
                {annotation.isVisible ? 'visibility' : 'visibility_off'}
              </span>
            </span>
          </div>
        ))}
      </PanelBarItem>
    </PanelBar>
  );
};
